import { env } from '@noor/config';
import type { ModerationEntity } from '../model/schema';
import { entityBag } from './entity-bag';

export interface EntityImage {
  url: string;
  type: string;
  position: number;
}

const SINGLE_IMAGE_FIELDS = [
  'icon',
  'background_image',
  'background',
  'logo',
  'image',
  'photo',
  'cover',
  'banner',
] as const;

/**
 * Dev S3 public base. Mirrors the `VITE_CORE_S3_BASE_URL` default so unit
 * tests (and any non-Vite consumer) resolve the same URLs; the app itself
 * reads the env value first via `storageBaseUrl()`.
 */
export const CORE_S3_BASE_FALLBACK = 'https://s3.test.api.noor.uz/core';

/** Effective S3 public base: env override wins, dev bucket is the fallback. */
export function storageBaseUrl(): string {
  const configured = (env.VITE_CORE_S3_BASE_URL ?? '').trim();
  return (configured !== '' ? configured : CORE_S3_BASE_FALLBACK).replace(/\/+$/, '');
}

/**
 * Change-request image payloads carry bare storage keys
 * (`restaurant/<uuid>`) while live entities carry full URLs. Absolute URLs
 * (and data/blob URLs) pass through; anything else is joined onto the S3
 * public base so `<img src>` never receives a bare path.
 */
export function resolveStorageUrl(link: string | null | undefined): string {
  const trimmed = link?.trim() ?? '';
  if (trimmed === '') return '';
  if (/^(https?:\/\/|data:|blob:)/i.test(trimmed)) return trimmed;
  return `${storageBaseUrl()}/${trimmed.replace(/^\/+/, '')}`;
}

/**
 * All gallery images, ordered by `position` (MAIN first by convention).
 *
 * Restaurants carry `icon` / `background_image` plain-string URLs instead of
 * an `images` array — those (plus other single-URL picture fields) are
 * appended after the array entries so the modal gallery never renders empty
 * while the API clearly sent pictures.
 */
export function entityImagesDetailed(entity: ModerationEntity): EntityImage[] {
  const bag = entityBag(entity);
  const fromArray: EntityImage[] = Array.isArray(bag.images)
    ? (bag.images as unknown[]).flatMap((item): EntityImage[] => {
        if (item === null || typeof item !== 'object') return [];
        const itemBag = item as Record<string, unknown>;
        if (typeof itemBag.url !== 'string' || itemBag.url === '') return [];
        const position = typeof itemBag.position === 'number' ? itemBag.position : 0;
        return [
          {
            url: resolveStorageUrl(itemBag.url),
            type: typeof itemBag.image_type === 'string' ? itemBag.image_type : '',
            position: Number.isFinite(position) ? position : 0,
          },
        ];
      })
    : [];
  const seen = new Set(fromArray.map((image) => image.url));
  const singles: EntityImage[] = [];
  for (const field of SINGLE_IMAGE_FIELDS) {
    const value: unknown = bag[field];
    if (typeof value !== 'string' || value.trim() === '') continue;
    if (seen.has(value)) continue;
    seen.add(value);
    singles.push({
      url: resolveStorageUrl(value),
      type: field,
      position: fromArray.length + singles.length,
    });
  }
  return [...fromArray.sort((a, b) => a.position - b.position), ...singles];
}

/** Image URLs (`images: [{url}]`) for the row thumbnail. */
export function entityImageUrls(entity: ModerationEntity): string[] {
  return entityImagesDetailed(entity).map((image) => image.url);
}

/** First displayable picture of an entity bag — thumbnail resolution in one place. */
export function firstEntityImageUrl(entity: ModerationEntity): string | undefined {
  return entityImagesDetailed(entity)[0]?.url;
}
